import { fetchEventSource } from '@microsoft/fetch-event-source';
import md5 from 'md5';

import { isNative } from '@whelk/const/src/env';
import { MAIN_ROBOT_INFO } from '@whelk/const/src/robot';
import {
  NETWORK_ERROR,
  NETWORK_ERROR_MAP,
  UNAUTHORIZED_CODE,
  NETWORK_SUCCESS_CODE,
} from '@whelk/const/src/services';
import { LocalStorageTokenKey } from '@whelk/const/src/token';
import axios from '@whelk/request/src/services/axios';
import { handleSendTextSseFetchEncrypt } from '@whelk/request/src/services/encrypt';
import { getNativePublicHeader } from '@whelk/request/src/services/publicParams';
import { addSseUrlPublicParams, sseFetchIntercept } from '@whelk/request/src/services/sseFetch';
import type { ApiResp } from '@whelk/services/src/common';
import type { SearchModeEnum } from '@whelk/store/slices/chatListSlice';
import { openBannedAppealModal } from '@whelk/utils/src/ban';
import { callNativeLoginOut, logoutCallback } from '@whelk/utils/src/logout';
import { trackCustomEvent } from '@whelk/utils/src/track';

import {
  AudioSeeMessageData,
  ChatMessage,
  CloseSseMessageData,
  FavoriteStatusEnum,
  FormItemTypeEnum,
  GetTargetChatHistoryData,
  GetTTSVoiceData,
  HistoryListByPageRespDto,
  LoadingSseMessageData,
  MultiModalFormItem,
  NEED_LOGIN_ERROR_CODE,
  PolicyInfo,
  PostSendTextSseExtraData,
  PostUploadSpiderData,
  RobotCustomConfig,
  SideCarRes,
  TEXT_SSE_TYPE,
  TextSseData,
  TextSseMessageData,
  TextSseSendData,
  TextSseUrlStatusData,
  TextStyleData,
  URL_LOAD_STATUS,
  USER_BANNED_ERROR_CODE,
  XxDownloadLinks,
} from './type/chat';

// 对话反馈
export async function postMsgFeedback(params: {
  feedbackType: 2 | 0; // 点踩 | 取消点踩
  dislikeReason: string[];
  msgID: string;
}) {
  const url = '/v1/api/chat/feedback';
  const { data: resp } = await axios.put<ApiResp<void>>(url, params);
  return resp.data;
}

// 信号收集反馈
export async function postSignalFeedback(params: {
  feedbackType: number; // 点踩
  action: number;
  messageId: string;
}) {
  const url = '/public/api/chat/feedback';
  const { data: resp } = await axios.post<ApiResp<void>>(url, params);
  return resp.data;
}

// 获取对话列表，分页接口
export async function getChatHistoryListByPage(
  lastID = '0',
  limit = 20,
  favoriteStatus?: FavoriteStatusEnum,
) {
  const url = `/v2/api/chat/histories/cursor`;
  const params = {
    characterID: MAIN_ROBOT_INFO.robotIDStr,
    lastID,
    limit,
    favoriteStatus: favoriteStatus ?? 0,
  };
  const { data: resp } = await axios.get<ApiResp<HistoryListByPageRespDto>>(url, { params });
  return resp.data;
}

// 搜索对话历史
export async function searchChatHistoryList(keyword: string) {
  const url = `/v2/api/chat/histories/search`;
  const { data: resp } = await axios.get<ApiResp<HistoryListByPageRespDto>>(url, {
    params: {
      content: keyword,
    },
  });
  return resp.data;
}

// 获取对话详情列表
export async function getTargetChatHistory(id: string) {
  const url = `/v2/api/chat/history/${id}`;
  const { data: resp } = await axios.get<ApiResp<GetTargetChatHistoryData>>(url);
  return resp.data;
}

// 修改对话标题
export async function renameChatTitle(chatID: string, title: string) {
  const url = '/v2/api/chat/rename_chat';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    chatID,
    title,
  });
  return resp.data;
}

// 删除对话
export async function deleteChatHistory(chatID: string) {
  const url = `/v1/api/chat/history/${chatID}`;
  const { data: resp } = await axios.delete<ApiResp<void>>(url);
  return resp.data;
}

// 删除全部历史对话
export async function deleteAllHistory() {
  const url = '/v1/api/chat/delete_chats';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    deleteAll: 1,
  });
  return resp.data;
}

// 批量删除对话
export async function deleteChatHistories(chatIDs: string[]) {
  const url = '/v1/api/chat/delete_chats';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    chatIDs,
    deleteAll: 0,
  });
  return resp.data;
}

// 收藏对话
export async function setChatFavoriteStatus(chatIDs: string[], favoriteStatus: FavoriteStatusEnum) {
  const url = '/v2/api/chat/favorite_chats';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    chatIDs,
    favoriteStatus,
  });
  return resp.data;
}

// 用户最终选择的消息ID
export async function putUserSelectFeedBack(params: {
  chatID: string;
  parentID: string;
  systemMsgID: string;
}) {
  const url = '/v1/api/chat/msg_choice';
  const { data: resp } = await axios.put<ApiResp<void>>(url, params);
  return resp.data;
}

// 获取活动开关
export async function getSwitchStatus() {
  const url = '/v1/api/push/switch_status';
  const { data: resp } = await axios.get<ApiResp<{ marketActivitySwitchStatus: number }>>(url);
  return resp.data;
}

// 置顶对话
export async function postChatTop(params: { chatID: string }) {
  const url = '/v1/api/chat/top';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    ...params,
  });
  return resp.data;
}

// 取消置顶对话
export async function deleteChatTop(params: { chatID: string }) {
  const url = `/v1/api/chat/top`;
  const { data: resp } = await axios.delete<ApiResp<void>>(url, {
    data: {
      ...params,
    },
  });
  return resp.data;
}

// 语音合成 TTS
export async function getMessageTTS(params: { msgID: string; timbre: string }) {
  const url = '/v1/api/chat/msg_tts';
  const { data: resp } = await axios.get<ApiResp<GetTTSVoiceData>>(url, {
    params,
  });
  return resp.data;
}

// 获取首页下载链接
export async function getXxDownloadLinks(xx: string) {
  const url = `/public/app/download_url?xx=${xx}`;
  const { data: resp } = await axios.get<ApiResp<XxDownloadLinks>>(url);
  return resp.data;
}

// 获取应事机器人个性化配置信息
export async function getYingshiCustomConfig() {
  // 应事机器人robotID 为 1
  const url = `/v1/api/chat/robot_custom_config?robotID=1`;
  const { data: resp } = await axios.get<ApiResp<RobotCustomConfig>>(url);
  return resp.data;
}

// 更新应事机器人个性化配置信息
export async function postUpdateYingshiCustomConfig(config: { robotVoiceID: string }) {
  // 应事机器人robotID 为 1
  const url = `/v1/api/chat/update_robot_custom_config`;
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    robotID: '1',
    config,
  });
  return resp.data;
}

// 获取对话分享链接
export async function getShareLink(params: { msgIDs: string[] }) {
  const url = '/v1/api/chat/share_messages';
  const { data: resp } = await axios.post<ApiResp<{ ShareUrl: string }>>(url, {
    ...params,
  });
  return resp.data;
}

// 获取删除对话
export async function postDeleteMessage(params: { msgIDs: string[] }) {
  const url = '/v1/api/chat/delete_messages';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    ...params,
  });
  return resp.data;
}

export async function postSendTextSse(params: {
  body: {
    model?: string;
    characterID: string;
    msgContent: string;
    chatID: string;
    multiModalList: MultiModalFormItem[];
    exampleID: string | undefined;
    voiceBytes?: string | null;
    searchMode?: SearchModeEnum;
    chatScene?: number | null;
    isAutoPlay?: number | null;
    playSpeedLevel?: number | null;
  };
  extraData: PostSendTextSseExtraData;
  signal: AbortSignal;
  onSendResultMessage: (data: TextSseSendData, extraData: PostSendTextSseExtraData) => void;
  onMessage: (
    data: TextSseMessageData | LoadingSseMessageData,
    isFirstTokenChunk?: boolean,
  ) => void;
  onAudioMessage?: (data: AudioSeeMessageData) => void;
  onMessageError: () => void;
  onSendError: (
    extraData: PostSendTextSseExtraData,
    code: any,
    cause: string,
    message?: string,
  ) => void;
  onFollowUp: (data: TextSseMessageData) => void;
  onUrlStatus: (data: TextSseUrlStatusData) => void;
  onCloseMessage?: (data: CloseSseMessageData) => void;
  handleMessageTrack: (
    message: ChatMessage,
    isFirstChunk: boolean,
    isSearch: boolean,
    exampleID?: string,
    msgContent?: string,
  ) => void;
}) {
  const {
    body,
    signal,
    extraData,
    onMessage,
    onAudioMessage,
    onMessageError,
    onSendError,
    onFollowUp,
    onUrlStatus,
    onCloseMessage,
    onSendResultMessage,
    handleMessageTrack,
  } = params;
  const url = '/v4/api/chat/msg';
  const formData = new FormData();
  formData.append('characterID', body.characterID);
  formData.append('msgContent', body.msgContent);
  formData.append('chatID', body.chatID);
  formData.append('model', body?.model ?? '');

  if (body?.searchMode) {
    formData.append('searchMode', body.searchMode);
  }
  if (body?.chatScene) {
    formData.append('chatScene', body.chatScene + '');
  }
  if (extraData?.bridgeOriginData?.sugOption?.sceneID) {
    formData.append('sceneID', extraData?.bridgeOriginData?.sugOption?.sceneID);
  }
  if (body?.isAutoPlay) {
    formData.append('isAutoPlay', body.isAutoPlay + '');
  }
  if (body?.playSpeedLevel) {
    formData.append('playSpeedLevel', body.playSpeedLevel + '');
  }
  // formData类型在网络传输的时候 会把\n 变成\r\n，并可能会在前后加上换行符 导致后端接收到内容与前端不一致 加密数据对不上
  // 加密时候 去掉 换行符
  const trimTextValue = body.msgContent.replace(/(\r\n|\n|\r)/g, '');
  let bodyMd5 = md5(body.characterID) + md5(trimTextValue) + md5(body.chatID);
  let multiForm: {
    name: string;
    formType?: FormItemTypeEnum;
    content?: string;
    fileID?: string;
  }[];
  if (extraData.isMultiModal && body.multiModalList) {
    multiForm = body.multiModalList;
    formData.append('form', JSON.stringify(multiForm));
    // @ts-ignore
    bodyMd5 += md5(JSON.stringify(multiForm));
  } else {
    bodyMd5 += md5('');
  }
  if (body.voiceBytes) {
    formData.append('voiceBytes', body.voiceBytes);
  }

  const time = Date.parse(new Date().toString());
  const addFetchParamsUrl = addSseUrlPublicParams(url, time);
  const yy = await handleSendTextSseFetchEncrypt(addFetchParamsUrl, time.toString(), bodyMd5);
  const responseData = [] as TextSseData[];
  const urlStatusData = [] as TextSseUrlStatusData[];
  let isError = false;
  fetchEventSource(addFetchParamsUrl, {
    method: 'POST',
    headers: {
      ...getNativePublicHeader(),
      yy: (yy as any) ?? '',
      token: window.localStorage.getItem(LocalStorageTokenKey) ?? '',
    },
    body: formData,
    signal,
    openWhenHidden: true,
    onopen: async (response) => {
      if (response.status === UNAUTHORIZED_CODE) {
        isNative ? callNativeLoginOut() : logoutCallback();
      } else if (response.status !== NETWORK_SUCCESS_CODE) {
        isError = true;
        onSendError(extraData, response.status, NETWORK_ERROR);
      }
    },
    onclose: () => {
      if (responseData.length <= 1 && !isError) {
        onSendError(extraData, null, 'close error');
      } else if (!isError) {
        onMessageError();
      }
    },
    onerror: (err) => {
      const messageList = responseData?.filter((item) => item.type === TEXT_SSE_TYPE.MESSAGE);
      if (messageList.length === 0 && !isError) {
        isError = true;
        const networkError = NETWORK_ERROR_MAP?.includes(err?.message);
        onSendError(
          extraData,
          null,
          networkError ? NETWORK_ERROR : err?.message,
          networkError ? NETWORK_ERROR : '',
        );
      } else if (!isError) {
        isError = true;
        onMessageError();
      }
      throw err;
    },
    onmessage: (msg) => {
      const result = JSON.parse(msg?.data ?? '') as TextSseData;
      if (result.type === TEXT_SSE_TYPE.SEND) {
        responseData.push(result);
        if (
          urlStatusData.some((chunk) =>
            chunk.data.urlStatus?.urlLoadStatus?.some(
              (item) => item.status === URL_LOAD_STATUS.FAIL,
            ),
          )
        ) {
          trackCustomEvent({
            id: 'url_access_fail',
            chat_id: (result as TextSseSendData).data.sendResult.chatID,
            msg_id: (result as TextSseSendData).data.sendResult.userMsgID,
          });
        }
        if (result.statusInfo.code === 0) {
          onSendResultMessage(result as TextSseSendData, extraData);
        } else {
          isError = true;
          if (result?.statusInfo?.code === NEED_LOGIN_ERROR_CODE) {
            isNative ? callNativeLoginOut() : logoutCallback({ skipPage: false });
          }
          if (result?.statusInfo?.code === USER_BANNED_ERROR_CODE) {
            openBannedAppealModal(result?.statusInfo?.message);
          }
          onSendError(
            extraData,
            result?.statusInfo?.code,
            result?.statusInfo?.message,
            result?.statusInfo?.message,
          );
        }
      } else if ([TEXT_SSE_TYPE.MESSAGE, TEXT_SSE_TYPE.LOADING].includes(result.type)) {
        responseData.push(result);
        const messageList =
          responseData?.filter((item) =>
            [TEXT_SSE_TYPE.MESSAGE, TEXT_SSE_TYPE.LOADING].includes(item.type),
          ) || [];
        const contentMessageList = messageList.filter((item) =>
          Boolean((item as TextSseMessageData)?.data?.messageResult?.content),
        );
        const isFirstTokenChunk = contentMessageList.length === 1;
        const isSearch = messageList.some(
          (item) =>
            (item as TextSseMessageData)?.data?.messageResult?.extra?.responseType === 'search',
        );
        onMessage(result as TextSseMessageData | LoadingSseMessageData);

        // 埋点处理 别问我为甚么加了个body.exampleID和body.msgContent，问就是又臭又硬的埋点
        handleMessageTrack(
          (result as TextSseMessageData)?.data?.messageResult,
          isFirstTokenChunk,
          isSearch,
          body.exampleID,
          body.msgContent,
        );
      } else if (result.type === TEXT_SSE_TYPE.FOLLOW_UP) {
        onFollowUp(result as TextSseMessageData);
      } else if (result.type === TEXT_SSE_TYPE.URL_STATUS) {
        urlStatusData.push(result as TextSseUrlStatusData);
        onUrlStatus(result as TextSseUrlStatusData);
      } else if (result.type === TEXT_SSE_TYPE.AUDIO) {
        if (isNative) {
          onAudioMessage?.(result as AudioSeeMessageData);
        }
      } else if (result.type === TEXT_SSE_TYPE.CLOSE) {
        if (isNative) {
          onCloseMessage?.(result as CloseSseMessageData);
        }
      }
    },
  });
}

// 重试消息
export async function postRetrySse(params: {
  retryBody: {
    model?: string;
    chatID: string;
    systemMsgID: string;
    userMsgID: string;
    characterID?: string;
    searchMode?: SearchModeEnum;
    retryType?: number;
    content?: string;
    isAutoPlay?: number | null;
    playSpeedLevel?: string | null;
  };
  signal: AbortSignal;
  onSendResultMessage: (data: TextSseSendData) => void;
  onMessage: (
    data: TextSseMessageData | LoadingSseMessageData,
    isFirstTokenChunk?: boolean,
  ) => void;
  onAudioMessage?: (data: AudioSeeMessageData) => void;
  onMessageError: () => void;
  // hasChatNoMessage 有send消息 和 有 send消息没有message消息 处理不一样
  onSendError: (hasSendNoMessage: boolean, code: any, message?: string, content?: string) => void;
  onFollowUp: (data: TextSseMessageData) => void;
  onUrlStatus: (data: TextSseUrlStatusData) => void;
  onCloseMessage?: (data: CloseSseMessageData) => void;
  handleMessageTrack: (message: ChatMessage, isFirstTokenChunk: boolean, isSearch: boolean) => void;
}) {
  const {
    retryBody,
    signal,
    onSendResultMessage,
    onMessage,
    onAudioMessage,
    onFollowUp,
    onUrlStatus,
    onMessageError,
    onSendError,
    onCloseMessage,
    handleMessageTrack,
  } = params;

  const responseData = [] as TextSseData[];
  let isError = false;

  const onErrorCallBack = (err?: any) => {
    const messageList = responseData?.filter((item) => item.type === TEXT_SSE_TYPE.MESSAGE);
    // send 消息异常走这里，否则如果下发了 message 不处理
    if (messageList.length === 0 && !isError) {
      isError = true;
      const sendList = responseData?.filter((item) => item.type === TEXT_SSE_TYPE.SEND);
      const hasSendList = sendList?.length > 0;
      const networkError = NETWORK_ERROR_MAP.includes(err?.message);
      if (err) {
        onSendError(hasSendList, null, networkError ? NETWORK_ERROR : '');
      } else {
        onSendError(hasSendList, null);
      }
    } else if (!isError) {
      onMessageError();
    }
  };

  return sseFetchIntercept(
    retryBody.content ? '/v4/api/chat/edit_msg' : '/v4/api/chat/retry_msg',
    retryBody,
    {
      method: 'POST',
      signal,
      openWhenHidden: true,
      onopen: async (response) => {
        if (response.status === UNAUTHORIZED_CODE) {
          logoutCallback();
        } else if (response.status !== NETWORK_SUCCESS_CODE) {
          isError = true;
          onSendError(false, response.status, NETWORK_ERROR, retryBody?.content);
        }
      },
      onclose: onErrorCallBack,
      onerror: (err) => {
        onErrorCallBack(err);
        throw new Error();
      },
      onmessage: (msg) => {
        const result = JSON.parse(msg?.data ?? '') as TextSseData;
        if (result.type === TEXT_SSE_TYPE.SEND) {
          responseData.push(result);
          if (result.statusInfo.code === 0) {
            onSendResultMessage(result as TextSseSendData);
          } else {
            isError = true;
            if (result.statusInfo.code === NEED_LOGIN_ERROR_CODE) {
              isNative ? callNativeLoginOut() : logoutCallback({ skipPage: false });
            }
            if (result?.statusInfo?.code === USER_BANNED_ERROR_CODE) {
              openBannedAppealModal(result?.statusInfo?.message);
            }
            onSendError(
              false,
              result?.statusInfo?.code,
              result?.statusInfo?.message,
              retryBody?.content,
            );
          }
        } else if ([TEXT_SSE_TYPE.MESSAGE, TEXT_SSE_TYPE.LOADING].includes(result.type)) {
          responseData.push(result);
          const messageList =
            responseData?.filter((item) => item.type === TEXT_SSE_TYPE.MESSAGE) || [];
          const contentMessageList = messageList.filter((item) =>
            Boolean((item as TextSseMessageData)?.data?.messageResult?.content),
          );
          const isFirstTokenChunk = contentMessageList.length === 1;
          const isSearch = messageList.some(
            (item) =>
              (item as TextSseMessageData)?.data?.messageResult?.extra?.responseType === 'search',
          );
          onMessage(result as TextSseMessageData | LoadingSseMessageData);
          // 埋点处理
          handleMessageTrack(
            (result as TextSseMessageData)?.data?.messageResult,
            isFirstTokenChunk,
            isSearch,
          );
        } else if (result.type === TEXT_SSE_TYPE.FOLLOW_UP) {
          onFollowUp(result as TextSseMessageData);
        } else if (result.type === TEXT_SSE_TYPE.URL_STATUS) {
          onUrlStatus(result as TextSseUrlStatusData);
        } else if (result.type === TEXT_SSE_TYPE.AUDIO) {
          if (isNative) {
            onAudioMessage?.(result as AudioSeeMessageData);
          }
        } else if (result.type === TEXT_SSE_TYPE.CLOSE) {
          if (isNative) {
            onCloseMessage?.(result as CloseSseMessageData);
          }
        }
      },
    },
  );
}

export async function getSideCar() {
  const url = `/v1/api/sidecar`;
  const { data: resp } = await axios.get<ApiResp<SideCarRes>>(url);
  return resp.data;
}

// 中断 重新拉取
export async function postRePullSse(params: {
  rePullBody: {
    msgID: string;
  };
  signal: AbortSignal;
  onFollowUp: (data: TextSseMessageData) => void;
  onUrlStatus: (data: TextSseUrlStatusData) => void;
  onMessage: (data: TextSseMessageData, isFirstTokenChunk: boolean) => void;
  onClose: () => void;
}) {
  const { rePullBody, signal, onMessage, onClose, onFollowUp, onUrlStatus } = params;

  let isError = false;

  const responseData = [] as any[];

  const handleError = () => {
    isError = true;
    if (!isError) {
      onClose();
    }
  };

  return sseFetchIntercept('/v4/api/chat/msg_fetch', rePullBody, {
    method: 'POST',
    signal,
    openWhenHidden: true,
    onopen: async (response) => {
      if (response.status === UNAUTHORIZED_CODE) {
        logoutCallback();
      }
    },
    onclose: handleError,
    onerror: handleError,
    onmessage: (msg) => {
      const result = JSON.parse(msg?.data ?? '');
      if (result.type === TEXT_SSE_TYPE.FOLLOW_UP) {
        onFollowUp(result as TextSseMessageData);
        return;
      } else if (result.type === TEXT_SSE_TYPE.URL_STATUS) {
        onUrlStatus(result as TextSseUrlStatusData);
        return;
      } else if ([TEXT_SSE_TYPE.MESSAGE, TEXT_SSE_TYPE.LOADING].includes(result.type)) {
        responseData.push(result);

        const messageList =
          responseData?.filter((item) => item.type === TEXT_SSE_TYPE.MESSAGE) || [];
        const contentMessageList = messageList.filter((item) =>
          Boolean((item as TextSseMessageData)?.data?.messageResult?.content),
        );
        const isFirstTokenChunk = contentMessageList.length === 1;

        onMessage(result as TextSseMessageData, isFirstTokenChunk);
      }
    },
  });
}

// 上传文件
export async function getFileRequestPolicy() {
  const url = `/v1/api/files/request_policy`;
  const { data: resp } = await axios.get<ApiResp<PolicyInfo>>(url);
  return resp.data;
}

export async function getPolicyCallback(params: {
  fileName: string;
  originFileName: string;
  dir: string;
  endpoint: string;
  bucketName: string;
  size: string;
  mimeType: string;
  fileMd5: string;
}) {
  const url = `/v1/api/files/policy_callback`;
  const { data: resp } = await axios.post<ApiResp<{ fileID: string; ossPath?: string }>>(url, {
    ...params,
  });
  return resp.data;
}

// 停止继续生成
export async function postStopContinueReply(msgID: string) {
  const url = '/v2/api/chat/stop_generating';
  const { data: resp } = await axios.post<ApiResp<void>>(url, {
    msgID,
    msgContent: '', // 后端不读，但是必须要有这个字段。。。 写死传
  });
  return resp.data;
}

// 获取客户端语音消息
export async function getNativeVoiceData(voiceId: string) {
  const res = await fetch(`/hailuo_bridge/voice?id=${voiceId}`);
  const arrayBuffer = await res?.arrayBuffer();
  return arrayBuffer;
}

// 检测用户要爬取的资源是否命中后端缓存
export async function postCheckCache(href: string) {
  const url = '/public/api/chat/cached';
  const { data: resp } = await axios.post<ApiResp<{ isCached: boolean }>>(url, {
    url: href,
  });
  return resp.data;
}

// 上传前端爬取到的文件到服务器
export async function postUploadSpider(formData: FormData) {
  const url = '/public/api/chat/context';
  try {
    const { data: resp } = await axios.post<ApiResp<PostUploadSpiderData>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return resp.statusInfo;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// 获取用户风格配置
export async function getTextStyleSelection() {
  const url = `/v1/api/user/config`;
  const { data: resp } = await axios.get<ApiResp<TextStyleData>>(url);
  return resp.data;
}

// 保存用户风格配置
export async function updataTextStyleSelection(params: {
  scene: {
    socialTalk: {
      styles: string[];
      length: string;
    };
  };
}) {
  const url = `/v1/api/user/config`;
  const { data: resp } = await axios.post<ApiResp<void>>(url, params);
  return resp.data;
}
